import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { apiFetch, clearLocalSession, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { AppleAuthRequest, AuthProvider, AuthRequest, AuthResponse } from "@/api/types";
import { isAuthError, platform, SECURE_KEY_REFRESH_TOKEN, type AppleSignInResult } from "@/platform";
import { analytics } from "@/analytics";
import { onboardingStep } from "@/app/onboarding";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";

export type LoginFailure = { kind: "rejoinBlocked"; until?: string } | { kind: "accountBlocked" } | { kind: "unavailable" } | { kind: "failed" } | null;

/** 온보딩 분기(2.1): consents → birthDate → profile → returnTo ?? /home. */
export function nextAfterLogin(user: NonNullable<AuthResponse["user"]>, returnTo: string | null): string {
  if (user.consentsRequired) return "/terms";
  if (user.birthDateRequired) return "/birth-date";
  if (user.profileRequired) return "/profile/new";
  return returnTo ?? "/home";
}

const endpoint: Record<AuthProvider, string> = { google: ep.authGoogle, apple: ep.authApple };
const providerLabel = (p: AuthProvider) => t(p === "apple" ? "login.provider.apple" : "login.provider.google");

/** 제공자별 ID 토큰 획득. Apple은 nonce(필수)도 함께 온다. */
type Credential = { idToken: string } | AppleSignInResult;

/**
 * F01-02·F01-02a. platform.auth.signInWith<Provider>() → POST /api/auth/<provider> {idToken, deviceId} (Apple은 +nonce) → { user, tokens? }.
 * 웹 Google은 예외로 서버 리다이렉트(startGoogleRedirect)로 떠났다가 /login?googleHandle로 돌아와 exchangeGoogleHandle로 같은 응답을 받는다.
 * tokens가 있으면(앱 Bearer) 저장, 없으면(웹 Cookie) user만. 모드 분기는 "tokens 유무" 하나뿐. 응답·온보딩·오류 처리는 제공자 공통.
 */
export function useSocialLogin(provider: AuthProvider) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<LoginFailure>(null);
  const showSnackbar = useUiStore((s) => s.showSnackbar);

  const complete = useCallback(
    async (res: AuthResponse) => {
      const auth = useAuthStore.getState();
      if (res.tokens) {
        auth.setSession(res.tokens);
        if (platform.clientKind !== "web") await platform.device.secureStore?.set(SECURE_KEY_REFRESH_TOKEN, res.tokens.refreshToken);
      }
      if (!res.user) throw new Error("auth response without user");
      auth.setLoginUser(res.user, provider);
      // 아이덴티티는 온보딩을 마친 회원만(가입 확정 전 pending 은 device_id 로만 남는다)
      if (onboardingStep(res.user) === null) analytics.setUserId(res.user.id);
      const ui = useUiStore.getState();
      const next = nextAfterLogin(res.user, ui.returnTo);
      // 온보딩이 남았으면 returnTo는 마지막 단계(프로필·웰컴)가 소비한다.
      if (next === ui.returnTo) ui.setReturnTo(null);
      navigate(next, { replace: true });
    },
    [navigate, provider],
  );

  /** 인증 요청 하나를 보내고 응답·오류를 제공자 공통으로 처리한다. */
  const run = useCallback(
    async (request: (deviceId: string) => Promise<AuthResponse>) => {
      setPending(true);
      setFailure(null);
      try {
        await complete(await request(await platform.device.getDeviceId()));
      } catch (e) {
        if (isApiError(e) && e.status === 403 && e.code === "REJOIN_BLOCKED") {
          const until = (e.details as { until?: string } | undefined)?.until;
          setFailure(until ? { kind: "rejoinBlocked", until } : { kind: "rejoinBlocked" });
        } else if (isApiError(e) && e.status === 403 && e.code === "ACCOUNT_BLOCKED") {
          await clearLocalSession();
          setFailure({ kind: "accountBlocked" });
        } else if (isApiError(e) && e.status === 503 && e.code === "MAINTENANCE") {
          navigate("/blocked?reason=maintenance", { replace: true });
        } else {
          setFailure({ kind: "failed" }); // F01-10 다이얼로그(네트워크·기타 공통)
        }
      } finally {
        setPending(false);
      }
    },
    [complete, navigate],
  );

  const exchange = useCallback(
    (credential: Credential) =>
      run((deviceId) => {
        const body: AuthRequest | AppleAuthRequest =
          provider === "apple" && "nonce" in credential
            ? {
                idToken: credential.idToken,
                deviceId,
                nonce: credential.nonce,
                // AC0-269: 서버가 refresh 토큰으로 교환해 두고 탈퇴 때 revoke
                ...(credential.authorizationCode ? { authorizationCode: credential.authorizationCode } : {}),
              }
            : { idToken: credential.idToken, deviceId };
        return apiFetch<AuthResponse>(endpoint[provider], { method: "POST", body, noAuth: true, noRefresh: true, skipCommonErrors: true });
      }),
    [provider, run],
  );

  /** 웹 Google 리다이렉트 복귀(auth-session 3.9): 서버 callback이 준 일회용 핸들 → 세션. 응답은 POST /auth/google과 같다. */
  const exchangeGoogleHandle = useCallback(
    (handle: string) =>
      run((deviceId) =>
        apiFetch<AuthResponse>(ep.authGoogleWeb, { method: "POST", body: { handle, deviceId }, noAuth: true, noRefresh: true, skipCommonErrors: true }),
      ),
    [run],
  );

  /** 웹 Google 리다이렉트 실패 복귀(?googleError). 처리는 signIn의 AuthError와 같다. */
  const failGoogleRedirect = useCallback(
    (code: string) => {
      if (code === "cancelled") return; // 조용히 종료
      if (code === "unavailable") {
        setFailure({ kind: "unavailable" });
        showSnackbar(t("login.unavailable", { provider: providerLabel("google") }));
      } else {
        setFailure({ kind: "failed" });
      }
    },
    [showSnackbar],
  );

  const signIn = useCallback(async () => {
    if (pending) return;
    if (provider === "google" && platform.auth.startGoogleRedirect) {
      setPending(true); // 페이지를 떠난다. 복귀는 LoginPage ?googleHandle
      platform.auth.startGoogleRedirect(useUiStore.getState().returnTo);
      return;
    }
    let credential: Credential;
    try {
      credential = provider === "apple" ? await platform.auth.signInWithApple() : await platform.auth.signInWithGoogle();
    } catch (e) {
      if (isAuthError(e) && e.code === "cancelled") {
        return; // 취소 → 무동작
      }
      if (isAuthError(e) && e.code === "unavailable") {
        setFailure({ kind: "unavailable" });
        showSnackbar(t("login.unavailable", { provider: providerLabel(provider) }));
        return;
      }
      setFailure({ kind: "failed" });
      return;
    }
    await exchange(credential);
  }, [exchange, pending, provider, showSnackbar]);

  return { provider, signIn, exchange, exchangeGoogleHandle, failGoogleRedirect, pending, failure, clearFailure: () => setFailure(null) };
}
